/**
 * Applies a booking update Milo read from a business's text to the traveler's trip (bookings + itinerary).
 * Pure functions: the test bench keeps the trip in the browser, so the app works out and applies the change.
 *
 * update: { action: 'add'|'change'|'cancel', type, name, reference, startDate, startTime, endDate, endTime, partySize }
 * Dates/times are local 'YYYY-MM-DD' / 'HH:MM'; bookings use 'YYYY-MM-DDTHH:MM'.
 */

const norm = (s) => String(s || '').toLowerCase().replace(/\(sample\)/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const digits = (s) => String(s || '').replace(/\D/g, '');
const related = (a, b) => {
  const x = norm(a);
  const y = norm(b);
  return Boolean(x && y && (x.includes(y) || y.includes(x)));
};

/** New 'YYYY-MM-DDTHH:MM' from an existing one plus whichever of date/time the text gave; null if no date at all. */
export function mergeDateTime(current, date, time) {
  const d = date || (current ? current.slice(0, 10) : '');
  if (!d) return null;
  return `${d}T${time || (current ? current.slice(11, 16) : '') || '00:00'}`;
}

/** Index of the booking the update is about: by reference, then name, then the sender's phone. -1 if none. */
export function findBooking(bookings, u, from = '') {
  const live = (b) => b.status !== 'cancelled';
  const tests = [
    (b) => u.reference && norm(b.confirmation) === norm(u.reference),
    (b) => u.name && related(b.name, u.name),
    (b) => digits(from) && digits(b.phone) === digits(from) && (!u.type || u.type === 'other' || b.type === u.type),
  ];
  for (const test of tests) {
    const i = bookings.findIndex((b) => live(b) && test(b));
    if (i >= 0) return i;
  }
  return -1;
}

/** The itinerary item for a booking at a given 'YYYY-MM-DDTHH:MM': same day, related title/place, closest time. */
function findItem(itinerary, name, at) {
  const day = itinerary?.days?.find((d) => d.date === at?.slice(0, 10));
  if (!day) return null;
  const matches = day.items.map((it, i) => ({ it, i })).filter(({ it }) => related(it.place, name) || related(it.title, name));
  if (!matches.length) return null;
  const exact = matches.find(({ it }) => it.time === at.slice(11, 16));
  return { day, ...(exact || matches[0]) };
}

/** Itinerary with one item moved to a new date/time (re-sorted), or marked cancelled. */
function editItinerary(itinerary, found, to, cancel = false) {
  const days = itinerary.days.map((d) => ({ ...d, items: [...d.items] }));
  const from = days.find((d) => d.date === found.day.date);
  const item = { ...from.items[found.i] };
  if (cancel) {
    item.cancelled = true;
    from.items[found.i] = item;
  } else {
    item.time = to.slice(11, 16);
    const target = days.find((d) => d.date === to.slice(0, 10)) || from;
    from.items.splice(found.i, 1);
    target.items.push(item);
    target.items.sort((a, b) => String(a.time).localeCompare(String(b.time)));
  }
  return { ...itinerary, days };
}

/**
 * What applying the update would do, or null when it changes nothing / can't be matched.
 * Returns { action, before, after, item: { title, from, to } | null, bookings, itinerary }.
 */
export function proposeUpdate(state, u, from = '') {
  if (!u || !['add', 'change', 'cancel'].includes(u.action)) return null;
  const bookings = state.bookings || [];
  const itinerary = state.itinerary;

  if (u.action === 'add') {
    const start = mergeDateTime(null, u.startDate, u.startTime);
    if (!start) return null;
    const existing = findBooking(bookings, u, from);
    if (existing >= 0 && bookings[existing].start === start) return null;
    const after = {
      type: u.type || 'other', name: u.name || 'Reservation', start, end: mergeDateTime(null, u.endDate, u.endTime),
      timezone: '', address: '', phone: from, confirmation: u.reference || '', party: u.partySize || '', source: 'sms',
    };
    let nextItinerary = itinerary;
    let item = null;
    const day = itinerary?.days?.find((d) => d.date === start.slice(0, 10));
    if (day && !findItem(itinerary, after.name, start)) {
      item = { title: after.name, from: null, to: start };
      nextItinerary = {
        ...itinerary,
        days: itinerary.days.map((d) => (d === day
          ? { ...d, items: [...d.items, { time: start.slice(11, 16), title: after.name, place: after.name, durationMin: null, transport: '', note: '' }].sort((a, b) => String(a.time).localeCompare(String(b.time))) }
          : d)),
      };
    }
    return { action: 'add', before: null, after, item, bookings: [...bookings, after], itinerary: nextItinerary };
  }

  const index = findBooking(bookings, u, from);
  if (index < 0) return null;
  const before = bookings[index];

  if (u.action === 'cancel') {
    const after = { ...before, status: 'cancelled' };
    const found = before.start && findItem(itinerary, before.name, before.start);
    return {
      action: 'cancel', before, after,
      item: found ? { title: found.it.title, from: before.start, to: null } : null,
      bookings: bookings.map((b, i) => (i === index ? after : b)),
      itinerary: found ? editItinerary(itinerary, found, null, true) : itinerary,
    };
  }

  const after = {
    ...before,
    start: u.startDate || u.startTime ? mergeDateTime(before.start, u.startDate, u.startTime) : before.start,
    end: u.endDate || u.endTime ? mergeDateTime(before.end, u.endDate, u.endTime) : before.end,
    party: u.partySize || before.party,
    confirmation: before.confirmation || u.reference || '',
  };
  if (after.start === before.start && after.end === before.end && after.party === before.party) return null;
  // The itinerary item that moves: the start time, or a hotel's check-out.
  const moved = after.start !== before.start ? [before.start, after.start] : before.type === 'hotel' && after.end !== before.end ? [before.end, after.end] : null;
  const found = moved && moved[0] && moved[1] && findItem(itinerary, before.name, moved[0]);
  return {
    action: 'change', before, after,
    item: found ? { title: found.it.title, from: `${found.day.date}T${found.it.time}`, to: moved[1] } : null,
    bookings: bookings.map((b, i) => (i === index ? after : b)),
    itinerary: found ? editItinerary(itinerary, found, moved[1]) : itinerary,
  };
}
